import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertOctagon, AlertTriangle, Info, RefreshCw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLoading, EmptyState } from "@/components/shared";
import { useReadOnly } from "./common";
import api from "@/lib/api";

const CFG = {
  critical: { icon: AlertOctagon, cls: "border-red-200 bg-red-50 text-red-700", dot: "bg-red-500" },
  warning: { icon: AlertTriangle, cls: "border-amber-200 bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  info: { icon: Info, cls: "border-slate-200 bg-slate-50 text-slate-600", dot: "bg-slate-400" },
};

export default function SubAlerts() {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/subcon/alerts").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const run = async () => { setBusy(true); try { await api.post("/subcon/alerts/run"); await load(); toast.success(t("sub.alRefreshed")); } catch { toast.error("Error"); } finally { setBusy(false); } };
  if (!data) return <PageLoading />;
  const groups = ["critical", "warning", "info"];
  return (
    <div data-testid="sub-alerts">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        {groups.map((g) => { const C = CFG[g]; return <span key={g} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${C.cls}`} data-testid={`alert-count-${g}`}><C.icon className="h-3.5 w-3.5" /> {t(`sub.al_${g}`)}: {data.counts[g] || 0}</span>; })}
        {!readOnly && <Button onClick={run} disabled={busy} variant="outline" className="ml-auto rounded-full" data-testid="alerts-refresh-btn"><RefreshCw className={`mr-1 h-4 w-4 ${busy ? "animate-spin" : ""}`} /> {t("sub.alRefresh")}</Button>}
      </div>
      {data.alerts.length === 0 ? <EmptyState icon={ShieldCheck} title={t("sub.alEmpty")} subtitle={t("sub.alEmptyHint")} /> : (
        <div className="space-y-2" data-testid="alerts-list">
          {data.alerts.map((a) => { const C = CFG[a.category] || CFG.info; return (
            <div key={a.id} className={`flex items-center gap-3 rounded-xl border bg-white p-3.5`} data-testid={`alert-${a.id}`}>
              <span className={`h-2 w-2 shrink-0 rounded-full ${C.dot}`} />
              <C.icon className={`h-4 w-4 shrink-0 ${a.category === "critical" ? "text-red-500" : a.category === "warning" ? "text-amber-500" : "text-slate-400"}`} />
              <div className="flex-1"><span className="text-sm font-medium text-navy">{a.message}</span></div>
              {a.due_date && <span className="text-xs text-slate-400">{a.due_date}</span>}
            </div>
          ); })}
        </div>
      )}
    </div>
  );
}
